import { useState, useMemo, useEffect, useCallback } from "react";

import { UserPermissions, getOrderedPermissions, UserRoles, pascalCaseToCamelCase } from "@simplyk/common";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";

import { AmplitudeEvents } from "../../../../constants/amplitude";
import { useCurrentUserContext } from "../../../../contexts/CurrentUserContext";
import { useLocaleContext } from "../../../../contexts/LocaleContext";
import { UserObject } from "../../../../gql/gql-types";
import { trpc } from "../../../../helpers/trpc";
import { useAmplitude } from "../../../../hooks/amplitude/useAmplitude";
import { useTranslate } from "../../../../hooks/useTranslate";

export interface AddUserAdministrator {
  firstName: string;
  lastName: string;
  email: string;
}

export interface UserManagementModalProps {
  title?: string;
  content?: UserObject;
  defaultPermissions?: UserPermissions[];
  isOpen: boolean;
  onClose: () => void;
  refetchTable?: () => Promise<void>;
  source?: "default" | "share-modal" | "bank-stepper";
  formLink?: string;
}

const getPermissionsListFromCheckPermissionsMap = (
  permissions: Record<UserPermissions, boolean>
): UserPermissions[] => {
  const hasFormAndPaymentPermission = Boolean(permissions[UserPermissions.Donation]);
  // We only display the Donation permission (ticketing is hidden), but it must activate both Donation and Ticketing
  const mappedPermissions = {
    ...permissions,
    Donation: hasFormAndPaymentPermission,
    Ticketing: hasFormAndPaymentPermission,
  };
  return Object.entries(mappedPermissions)
    .map(([key, value]) => (value ? key : null))
    .filter((val) => val !== null) as UserPermissions[];
};

export const useUserManagementModal = ({
  onClose,
  refetchTable,
  content,
  defaultPermissions,
  isOpen,
  source,
  formLink,
}: UserManagementModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { organization, currentUser, refetchUser, userPermissions: currentUserPermission } = useCurrentUserContext();
  const { t } = useTranslate();
  const [submitIsLoading, setSubmitIsLoading] = useState(false);
  const [step, setStep] = useState<"UPDATE_USERS" | "SMS_CODE_VERIFY">("UPDATE_USERS");
  const [code, setCode] = useState("");
  const isEditUser = Boolean(content);
  const { logAmplitudeEvent } = useAmplitude();
  const { mutateAsync: execCreateAdministratorUser } = trpc.addMemberToOrganization.useMutation();
  const { mutateAsync: execEditOrganizationMember } = trpc.editOrganizationMember.useMutation();

  const userGroup = useMemo(
    () => content?.organizationGroups?.find((group) => organization?.id === group.organizationId),
    [content?.organizationGroups, organization?.id]
  );
  const userRole = userGroup?.role;
  const userPermissions =
    isEditUser && userGroup
      ? userGroup.permissions
      : defaultPermissions || currentUserPermission || getOrderedPermissions(organization?.country);
  const getCheckPermissionsMapFromPermissionsList = (
    permissions: UserPermissions[]
  ): Record<UserPermissions, boolean> => {
    return permissions.reduce((acc, curr) => ({ ...acc, [curr]: true }), {} as Record<UserPermissions, boolean>);
  };

  const [permissions, setPermissions] = useState<Record<UserPermissions, boolean> | undefined>(
    isOpen ? getCheckPermissionsMapFromPermissionsList(userPermissions) : undefined
  );

  useEffect(() => {
    if (isOpen) {
      setPermissions(getCheckPermissionsMapFromPermissionsList(userPermissions));
    } else {
      setPermissions(undefined);
    }
  }, [isOpen, userPermissions]);

  useEffect(() => {
    if (isOpen) {
      setStep("UPDATE_USERS");
    }
  }, [isOpen]);

  const form = useForm<AddUserAdministrator>({
    defaultValues: {
      firstName: content?.firstName || "",
      lastName: content?.lastName || "",
      email: content?.email || "",
    },
    shouldUnregister: true,
  });

  useEffect(() => {
    if (content) {
      setStep("UPDATE_USERS");
      setCode("");

      form.reset({
        firstName: content?.firstName || "",
        lastName: content?.lastName || "",
        email: content?.email || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);
  const { locale } = useLocaleContext();

  const handlePermissionChange = useCallback(
    (values: Record<UserPermissions, boolean>) => {
      setPermissions({ ...permissions, ...values });
    },
    [permissions]
  );

  const onSubmit = form.handleSubmit(async (data: AddUserAdministrator) => {
    setSubmitIsLoading(true);
    if (!isEditUser) {
      // Track different events based on context
      if (source === "share-modal") {
        logAmplitudeEvent(AmplitudeEvents.InviteUserDuringActivationInvited, { user_type: "Team Member" });
      } else if (source === "bank-stepper") {
        logAmplitudeEvent(AmplitudeEvents.InviteUserDuringActivationInvited, { user_type: "Accountant" });
      } else {
        logAmplitudeEvent(AmplitudeEvents.NewUserInvited);
      }
      const { error, data: res } = await execCreateAdministratorUser({
        ...data,
        locale,
        permissions: permissions ? getPermissionsListFromCheckPermissionsMap(permissions) : [],
        code,
        source: source || "default",
        formLink,
      });

      if (error) {
        enqueueSnackbar(
          t(
            "dashboard",
            error.code === "userAlreadyExists"
              ? "settings.organization.userManagement.userAlreadyExists"
              : error.code === "userDoesNotHavePermission"
                ? "settings.organization.userManagement.userAlreadyExists"
                : error.code === "authenticate_sms_otp_code_incorrect"
                  ? "signin.twoFactor.wrongCode"
                  : "settings.organization.userManagement.otherError",
            {
              email: data.email,
            }
          ),
          { vibe: "danger" }
        );
      } else if (res?.requiresSmsCode) {
        setStep("SMS_CODE_VERIFY");
      } else {
        if (code) {
          logAmplitudeEvent(AmplitudeEvents.TwoFASuccessful, {
            moment: "user with bank rights invited",
          });
        }
        enqueueSnackbar(t("dashboard", `settings.organization.userManagement.creationSuccess`), { vibe: "positive" });
        refetchTable && (await refetchTable());
        onClose();
      }
    } else {
      logAmplitudeEvent(AmplitudeEvents.ExistingUserEdited);
      const { error, data: res } = await execEditOrganizationMember({
        id: content?.id as string,
        permissions: permissions ? getPermissionsListFromCheckPermissionsMap(permissions) : [],
        code,
      });
      if (error) {
        enqueueSnackbar(t("dashboard", "settings.organization.userManagement.editError"), { vibe: "danger" });
      } else if (res?.requiresSmsCode) {
        setStep("SMS_CODE_VERIFY");
      } else {
        if (code) {
          logAmplitudeEvent(AmplitudeEvents.TwoFASuccessful, {
            moment: "bank rights added to user",
          });
        }
        if (currentUser?.id === content?.id) {
          await refetchUser();
        }
        enqueueSnackbar(t("dashboard", "settings.organization.userManagement.editSuccess"), { vibe: "positive" });
        refetchTable && (await refetchTable());
        setSubmitIsLoading(false);
        onClose();
      }
    }
    setSubmitIsLoading(false);
  });

  const isUserOwner = userRole === UserRoles.Owner;

  const getPermissionCheckboxLabel = useCallback(
    (checkboxes: UserPermissions[]) => {
      return permissions
        ? checkboxes
            .filter((permission) => permission !== UserPermissions.Ticketing)
            .map((permission) => {
              const defaultChecked =
                permission === UserPermissions.Donation
                  ? permissions[UserPermissions.Donation] || permissions[UserPermissions.Ticketing]
                  : permissions[permission];
              return {
                label: t(
                  "dashboard",
                  `common.userPermissions.${pascalCaseToCamelCase(permission.toString())}` as never
                ),
                choice: permission,
                name: permission,
                defaultChecked,
                disabled: isUserOwner || !currentUserPermission.includes(permission),
              };
            })
        : undefined;
    },
    [t, permissions, isUserOwner, currentUserPermission]
  );

  return {
    form,
    isUserOwner: userRole === UserRoles.Owner,
    isEditUser,
    onSubmit,
    submitIsLoading,
    handlePermissionChange,
    getPermissionCheckboxLabel,
    step,
    code,
    setCode,
    maskedPhoneNumber: currentUser?.maskedPhoneNumber,
  };
};
